<script lang="ts">
  import type { DecodedImage } from '$lib/image/decode'
  import { toSvg } from '$lib/pipeline/export'
  import {
    DEFAULT_SVG_OPTIONS,
    type CropRect,
    type SvgMode,
    type VectorizationResult,
  } from '$lib/types'
  import CompareView from '$ui/CompareView.svelte'

  interface Props {
    result: VectorizationResult
    name: string
    /** The source image, shown alongside the result in the compare view. */
    image?: DecodedImage | null
    /** The region that was converted, if the user cropped before converting. */
    crop?: CropRect | null
  }

  const { result, name, image = null, crop = null }: Props = $props()

  let mode = $state<SvgMode>('centerline')
  // On by default: the first thing worth seeing in a result is how the drawing
  // was split into strokes, which a single colour hides completely.
  let colorize = $state(true)
  let copied = $state(false)

  /**
   * Two documents from the same strokes.
   *
   * Colour-coding is a way of *reading* the result — which stroke is which —
   * not a property of it. What leaves the app is plain black artwork, which is
   * what anyone opening the file in an editor expects; a rainbow drawing would
   * be a surprise to undo by hand.
   */
  const previewSvg = $derived(toSvg(result, { ...DEFAULT_SVG_OPTIONS, mode, colorize }))
  const exportSvg = $derived(toSvg(result, { ...DEFAULT_SVG_OPTIONS, mode, colorize: false }))
  const fileName = $derived(`${name.replace(/\.[^.]+$/, '') || 'drawing'}-${mode}.svg`)

  function download() {
    const blob = new Blob([exportSvg], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.click()
    // Revoked on the next tick: revoking synchronously can cancel the download
    // in some browsers before it has started reading the blob.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  async function copy() {
    await navigator.clipboard.writeText(exportSvg)
    copied = true
    setTimeout(() => (copied = false), 1500)
  }
</script>

<section class="result" data-testid="result-view">
  <div class="controls">
    <div class="group">
      <label for="svg-mode">Export</label>
      <select id="svg-mode" bind:value={mode} data-testid="export-mode">
        <option value="centerline">Editable centerlines</option>
        <option value="outline">Variable-width outlines</option>
      </select>
    </div>

    <div class="group">
      <input id="colorize" type="checkbox" bind:checked={colorize} data-testid="export-colorize" />
      <label for="colorize">Colour strokes <span class="hint">preview only</span></label>
    </div>

    <div class="spacer"></div>

    <button type="button" onclick={copy} data-testid="copy-svg">
      {copied ? 'Copied' : 'Copy SVG'}
    </button>
    <button type="button" class="primary" onclick={download} data-testid="download-svg">
      Download SVG
    </button>
  </div>

  {#if image}
    <CompareView {image} svg={previewSvg} {crop} />
  {:else}
    <!-- Generated by this app from its own data model; every interpolated value
         is escaped by the exporter. -->
    <div class="canvas" data-testid="svg-preview">{@html previewSvg}</div>
  {/if}

  {#if result.warnings && result.warnings.length > 0}
    <ul class="warnings" data-testid="result-warnings">
      {#each result.warnings as warning (warning)}
        <li>{warning}</li>
      {/each}
    </ul>
  {/if}

  <dl class="stats" data-testid="result-stats">
    <div><dt>Strokes</dt><dd data-testid="stat-strokes">{result.strokes.length}</dd></div>
    <div><dt>Working size</dt><dd>{result.width} × {result.height}</dd></div>
    {#if result.iterations > 0}
      <div><dt>Iterations</dt><dd data-testid="stat-iterations">{result.iterations}</dd></div>
      <div><dt>Loss (MSE)</dt><dd data-testid="stat-loss">{result.loss.toFixed(5)}</dd></div>
    {/if}
    <div>
      <dt>Time</dt>
      <dd>{Math.round(Object.values(result.timings).reduce((a, b) => a + b, 0))} ms</dd>
    </div>
  </dl>
</section>

<style>
  .result {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
  }

  .group {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .spacer {
    flex: 1;
  }

  label {
    color: var(--text-muted);
    font-size: 0.9rem;
  }

  .hint {
    opacity: 0.7;
    font-size: 0.78rem;
  }

  select {
    padding: 0.3rem 0.5rem;
    color: inherit;
    background: var(--surface-raised);
    border: 1px solid var(--border-strong);
    border-radius: 6px;
  }

  button {
    padding: 0.4rem 0.8rem;
    background: var(--surface-raised);
    border: 1px solid var(--border-strong);
    border-radius: 8px;
  }

  button.primary {
    background: var(--accent-strong);
    border-color: var(--accent-strong);
    color: #fff;
  }

  .canvas {
    display: flex;
    justify-content: center;
    padding: 0.5rem;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }

  .canvas :global(svg) {
    width: 100%;
    height: auto;
    max-height: 70vh;
  }

  .warnings {
    margin: 0;
    padding: 0.5rem 1rem 0.5rem 2rem;
    color: var(--text-muted);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 0.85rem;
  }

  .stats {
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem;
    margin: 0;
    font-size: 0.85rem;
  }

  .stats div {
    display: flex;
    gap: 0.4rem;
  }

  dt {
    color: var(--text-muted);
  }

  dd {
    margin: 0;
    font-family: var(--mono);
  }
</style>
